import { Card, SectionTitle, InfoIcon, RowPop, NotEnoughData, Tip } from "./ui";
import { TEACHERS_BY_SCHOOL, MIN_STUDENTS, type Variant } from "../data/data";
import { TIER_COLORS } from "../data/tokens";

const TIERS = [
  {
    key: "onTrack",
    label: "On track",
    color: TIER_COLORS.onTrack,
    desc: "At or above grade-level expectations.",
  },
  {
    key: "borderline",
    label: "Borderline",
    color: TIER_COLORS.borderline,
    desc: "Just below grade level; close to on track.",
  },
  {
    key: "notOnTrack",
    label: "Not on track",
    color: TIER_COLORS.notOnTrack,
    desc: "Meaningfully below grade-level expectations.",
  },
  {
    key: "notStarted",
    label: "Not Started",
    color: TIER_COLORS.notStarted,
    desc: "No assessed progress yet this year.",
  },
] as const;

type GrowthDist = {
  onTrack: number;
  borderline: number;
  notOnTrack: number;
  notStarted: number;
};
interface Snapshot {
  label: string;
  date: string;
  dist: GrowthDist;
  current?: boolean;
}
const GROWTH_SNAPSHOTS: Snapshot[] = [
  {
    label: "Aug",
    date: "'26",
    dist: { onTrack: 12, borderline: 26, notOnTrack: 27, notStarted: 35 },
  },
  {
    label: "Sep",
    date: "'26",
    dist: { onTrack: 16, borderline: 28, notOnTrack: 28, notStarted: 28 },
  },
  {
    label: "Oct",
    date: "'26",
    dist: { onTrack: 21, borderline: 29, notOnTrack: 28, notStarted: 22 },
  },
  {
    label: "Nov",
    date: "'26",
    dist: { onTrack: 27, borderline: 30, notOnTrack: 27, notStarted: 16 },
  },
  {
    label: "Dec",
    date: "'26",
    dist: { onTrack: 32, borderline: 31, notOnTrack: 25, notStarted: 12 },
  },
  {
    label: "Jan",
    date: "'27",
    dist: { onTrack: 38, borderline: 31, notOnTrack: 23, notStarted: 8 },
  },
  {
    label: "Feb",
    date: "'27",
    dist: { onTrack: 44, borderline: 30, notOnTrack: 20, notStarted: 6 },
  },
  {
    label: "Mar",
    date: "'27",
    dist: { onTrack: 50, borderline: 29, notOnTrack: 17, notStarted: 4 },
  },
  {
    label: "Apr",
    date: "'27",
    dist: { onTrack: 56, borderline: 27, notOnTrack: 14, notStarted: 3 },
  },
  {
    label: "May",
    date: "'27",
    dist: { onTrack: 62, borderline: 25, notOnTrack: 11, notStarted: 2 },
    current: true,
  },
];

const fmtN = (n: number) => n.toLocaleString("en-US");

function GrowthBar({
  snap,
  sampleSize,
  tipAlign = "center",
}: {
  snap: Snapshot;
  sampleSize: number;
  tipAlign?: "center" | "left" | "right";
}) {
  const present = TIERS.filter((b) => snap.dist[b.key] > 0);
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center gap-2.5">
      <div className="text-center leading-[1.05]">
        <div className="font-libre-baskerville text-2xl font-normal text-[#28543D]">
          {snap.dist.onTrack}%
        </div>
        <div className="text-[11.5px] text-[#687368]">On track</div>
      </div>
      <div
        className={`flex h-[184px] w-full max-w-[58px] flex-col rounded-[6px] ${
          snap.current ? "border-2 border-[#28543D]" : "border border-[#DED8CC]"
        }`}
      >
        {present.map((b, i) => {
          const v = snap.dist[b.key];
          const count = Math.round((v / 100) * sampleSize);
          const isFirst = i === 0;
          const isLast = i === present.length - 1;
          const label = (
            <span>
              <strong className="mb-0.5 block">
                {b.label} ({v}%)
              </strong>
              {fmtN(count)} of {fmtN(sampleSize)} students
              <span className="mt-1 block font-medium text-[#687368]">
                {b.desc}
              </span>
            </span>
          );
          return (
            <Tip
              key={b.key}
              label={label}
              align={tipAlign}
              className="block w-full"
              style={{ height: `${v}%` }}
            >
              <div
                className="h-full w-full cursor-help"
                style={{
                  background: b.color,
                  borderTopLeftRadius: isFirst ? 7 : 0,
                  borderTopRightRadius: isFirst ? 7 : 0,
                  borderBottomLeftRadius: isLast ? 7 : 0,
                  borderBottomRightRadius: isLast ? 7 : 0,
                }}
              />
            </Tip>
          );
        })}
      </div>
      <div className="text-center leading-[1.2]">
        <div
          className={`text-xs font-bold ${snap.current ? "text-[#28543D]" : "text-[#294B39]"}`}
        >
          {snap.label}
        </div>
        <div className="text-[10.5px] text-[#687368]">{snap.date}</div>
      </div>
    </div>
  );
}

function TierLegend({ align = "right" }: { align?: "center" | "right" }) {
  return (
    <div className="flex flex-wrap gap-3.5">
      {TIERS.map((t) => (
        <Tip
          key={t.key}
          align={align}
          label={
            <span>
              <strong className="mb-0.5 block">{t.label}</strong>
              {t.desc}
            </span>
          }
        >
          <span className="flex cursor-help items-center gap-1.5">
            <span
              className="h-[11px] w-[11px] rounded-[3px]"
              style={{ background: t.color }}
            />
            <span className="text-xs font-semibold text-[#687368]">
              {t.label}
            </span>
          </span>
        </Tip>
      ))}
    </div>
  );
}

function ProficiencyGrowth({ sampleSize }: { sampleSize: number }) {
  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[15px] font-bold text-[#294B39]">
            School-wide proficiency
          </span>
          <InfoIcon text="Monthly distribution of student proficiency from August 2026 through May 2027." />
        </div>
        <TierLegend />
      </div>

      <div className="grid grid-cols-[1fr_180px] items-center gap-6">
        <div className="grid grid-cols-10 items-end gap-2.5 pt-2">
          {GROWTH_SNAPSHOTS.map((s, i) => {
            const tipAlign =
              i <= 1
                ? "left"
                : i >= GROWTH_SNAPSHOTS.length - 2
                  ? "right"
                  : "center";
            return (
              <GrowthBar
                key={s.label}
                snap={s}
                sampleSize={sampleSize}
                tipAlign={tipAlign}
              />
            );
          })}
        </div>
        <div className="flex flex-col gap-3 border-l border-[#DED8CC] pl-6">
          <div>
            <div className="font-libre-baskerville text-[30px] leading-none text-[#294B39]">
              {sampleSize.toLocaleString("en-US")}
            </div>
            <span className="text-[12.5px] text-[#687368]">
              students (updated daily)
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}

function schoolTiers(avgMastery: number) {
  const onTrack = Math.max(0, Math.min(100, Math.round(avgMastery)));
  const rem = 100 - onTrack;
  const notStarted = Math.min(rem, 4);
  const borderline = Math.round((rem - notStarted) * 0.58);
  const notOnTrack = rem - notStarted - borderline;
  return { onTrack, borderline, notOnTrack, notStarted };
}

function TierBar({
  tiers,
}: {
  tiers: {
    onTrack: number;
    borderline: number;
    notOnTrack: number;
    notStarted: number;
  };
}) {
  return (
    <div className="flex h-3.5 overflow-hidden rounded-md border border-[#DED8CC]">
      {TIERS.map((t) => {
        const val = tiers[t.key];
        if (!val) return null;
        return (
          <div
            key={t.key}
            title={`${t.label}: ${val}%`}
            className="border-r border-white/60"
            style={{ width: `${val}%`, background: t.color }}
          />
        );
      })}
    </div>
  );
}

function OnTrackBySchool({ variant }: { variant: Variant }) {
  const rows = [...variant.schools].sort((a, b) => b.avgMastery - a.avgMastery);
  return (
    <Card className="flex flex-col gap-3.5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[15px] font-bold text-[#294B39]">
            On track by school
          </span>
          <InfoIcon text="How each school's students split across on track, borderline, not on track, and not started. Create a category to group a school's not-on-track students." />
        </div>
        <TierLegend />
      </div>
      <div className="flex flex-col">
        {rows.map((s) => {
          const tiers = schoolTiers(s.avgMastery);
          const teacherItems = (TEACHERS_BY_SCHOOL[s.name] || []).map((n) => {
            const t = variant.teachers.find((tt) => tt.name === n);
            return { label: n, value: t ? `${t.onTrackPct}% on track` : "—" };
          });
          return (
            <div
              key={s.name}
              className="group/row relative m-0 grid items-center gap-3.5 border-t border-[#DED8CC] px-2 py-3.5"
              style={{
                gridTemplateColumns: "1.5fr 1fr 118px",
                marginInline: "-8px",
              }}
              tabIndex={0}
            >
              <span className="truncate text-[13.5px] font-bold text-[#294B39]">
                {s.name}
              </span>
              <TierBar tiers={tiers} />
              <span className="text-right text-[13.5px] text-[#294B39]">
                <strong className="font-bold">{tiers.onTrack}%</strong>{" "}
                <span className="text-[#687368]">on track</span>
              </span>
              <RowPop
                title="By teacher"
                items={
                  teacherItems.length
                    ? teacherItems
                    : [
                        { label: "On track", value: `${tiers.onTrack}%` },
                        { label: "Borderline", value: `${tiers.borderline}%` },
                        {
                          label: "Not on track",
                          value: `${tiers.notOnTrack}%`,
                        },
                        { label: "Not Started", value: `${tiers.notStarted}%` },
                      ]
                }
              />
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export function ProficiencySection({
  variant,
  hasData,
  sampleSize,
}: {
  variant: Variant;
  hasData: boolean;
  sampleSize: number;
}) {
  if (!hasData) return null;
  const enough = sampleSize >= MIN_STUDENTS;
  return (
    <section className="flex flex-col gap-4">
      <SectionTitle title="On track to year-end proficiency" />
      {!enough ? (
        <Card>
          <NotEnoughData message="Too few students in this selection to report proficiency reliably. Widen the filters to see grade and standard breakdowns." />
        </Card>
      ) : (
        <>
          <ProficiencyGrowth sampleSize={sampleSize} />
          <OnTrackBySchool variant={variant} />
        </>
      )}
    </section>
  );
}
